import React from "react";
import { AddIcon, CancelIcon, FilterIcon, ICON_SIZE } from "../actionIcons";
import { Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@radix-ui/themes";

import useServerSearch from "../hooks/useServerSearch";
import DensityToggle from "./DensityToggle";

/**
 * The one footer bar for every paginated sheet.
 *
 * Replaces seven near-identical inline copies (Customers/Brokers/Sellers/Cash/
 * CreditCleared/Cancelled/MessageHistory), which between them carried the old
 * DataTables-style "Show N per page … Showing 1 to 8 of 8 entries … First
 * Previous 1 Next Last" bar plus a Go/Clear search.
 *
 * Search is now live (see useServerSearch) so Go is gone; Clear survives only
 * on the deal sheets, where it also resets the date range.
 *
 * IDS ARE A CONTRACT. The Sales tour (useTabJoyride.js) spotlights
 * #sales-summary, #date-filters, #search-bar, #vehicle-count, #records-per-page,
 * #sheet-density-toggle, #page-info and #pagination-controls; the contact-sheet
 * tour (useCrmSheetTour.js) reuses most of them plus #sheet-add-new. Don't
 * rename them.
 */
export default function SheetFooter({
    summaryId,                    // #sales-summary / #cancelled-summary / …
    countLabel = "Records Displayed:",
    searchPlaceholder = "Search…",
    pagination, setPagination,
    onAdd, canAdd = false,
    dateFilter,                   // { from, to, setFrom, setTo, onApply, onClear }
    className = "",
    // Optional multi-select bar: { selection, onMessage, canMessage, moreThanPage }
    selectionBar,
}) {
    const { searchTerm, setSearchTerm, submitNow, clearSearch } =
        useServerSearch(pagination, setPagination);

    const totalPages = Math.max(1, pagination.totalPages || 1);
    const currentPage = pagination.currentPage || 1;

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            setPagination((prev) => ({ ...prev, currentPage: page }));
        }
    };

    const changePageSize = (e) => {
        setPagination((prev) => ({
            ...prev,
            recordsPerPage: parseInt(e.target.value, 10),
            currentPage: 1,   //a smaller page size can orphan the current page
        }));
    };

    //Only worth showing numbered buttons when there's somewhere to jump to.
    const pageNumbers = [];
    if (totalPages > 1) {
        const first = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
        for (let p = first; p <= Math.min(totalPages, first + 4); p++) pageNumbers.push(p);
    }

    //Two shells: the CRM sheets keep their Add button visible on mobile (the
    //CommonDataView toolbar covers search/paging there), while the deal sheets
    //hide the whole bar below 768px.
    const shell = dateFilter ? "sheet-footer sheet-footer--desktop" : "sheet-footer";

    const selectedCount = selectionBar?.selection?.ids?.size ?? 0;
    //The footer's height is fixed; the selection bar is an extra row inside it.
    //Without this the row is drawn past the bottom of the screen and takes the
    //pager with it — see .sheet-footer--selecting.
    const selecting = selectedCount > 0 ? " sheet-footer--selecting" : "";

    return (
        <div id={summaryId} className={`${shell}${selecting} ${className}`}>
            {/* Selection bar — only once something is ticked, so the footer keeps
                its usual shape until multi-select is actually in use. */}
            {selectedCount > 0 && (
                <div id="selection-bar" className="sheet-footer-row sheet-selection-bar">
                    <span id="selection-count">
                        <strong>{selectedCount}</strong> selected
                    </span>
                    {selectionBar.canMessage && (
                        <button id="message-selected" className="btn-t btn-t-primary"
                            onClick={() => selectionBar.onMessage(selectionBar.selection.ids)}>
                            Message selected
                        </button>
                    )}
                    <button id="clear-selection" className="btn-t btn-t-secondary"
                        onClick={selectionBar.selection.clear}><CancelIcon size={ICON_SIZE} />
                        Clear
                    </button>
                    {/* Points at the audience picker rather than offering a
                        "select all" this checkbox cannot honestly deliver: the
                        rows past this page are not loaded, and a cross-page
                        select would also have to reproduce the search filter. */}
                    {selectionBar.moreThanPage && (
                        <span className="text-sm text-[var(--theme-muted)]">
                            To reach everyone, use Messages → Compose and pick an audience.
                        </span>
                    )}
                </div>
            )}

            {/* Top row — actions, search, count */}
            <div className="sheet-footer-row sheet-footer-row--top">
                {dateFilter ? (
                    <div className="flex items-center space-x-2" id="date-filters">
                        <label className="text-sm font-medium text-[var(--theme-muted)]">From:</label>
                        <input id="date-from-input" type="date" className="input-t !w-auto"
                            value={dateFilter.from} onChange={(e) => dateFilter.setFrom(e.target.value)} />
                        <label className="text-sm font-medium text-[var(--theme-muted)]">To:</label>
                        <input id="date-to-input" type="date" className="input-t !w-auto"
                            value={dateFilter.to} onChange={(e) => dateFilter.setTo(e.target.value)} />
                        <button id="date-filter-button" className="btn-t btn-t-soft"
                            onClick={dateFilter.onApply}><FilterIcon size={ICON_SIZE} />Filter</button>
                        <button id="clear-filters-button" className="btn-t btn-t-secondary"
                            onClick={() => { clearSearch(); dateFilter.onClear(); }}><CancelIcon size={ICON_SIZE} />Clear</button>
                    </div>
                ) : canAdd ? (
                    <Button id="sheet-add-new" variant="soft" color="green" size="2" radius="medium" onClick={onAdd}>
                        <AddIcon size={ICON_SIZE} /> Add New
                    </Button>
                ) : <div />}

                <div className="hidden md:flex items-center space-x-2" id="search-bar">
                    <div className="sheet-search">
                        <Search size={15} className="sheet-search-icon" aria-hidden="true" />
                        <input
                            id="search-input"
                            type="text"
                            className="input-t sheet-search-input"
                            placeholder={searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") submitNow(); }}
                        />
                        {searchTerm !== "" && (
                            <button className="sheet-search-clear" onClick={clearSearch}
                                aria-label="Clear search"><X size={14} /></button>
                        )}
                    </div>
                </div>

                <div className="hidden md:flex items-center space-x-2" id="vehicle-count">
                    <span className="text-sm font-medium text-[var(--theme-muted)]">{countLabel}</span>
                    <span className="text-lg font-bold text-[var(--theme-accent)]">
                        {pagination.recordsOnCurrentPage}
                    </span>
                </div>
            </div>

            {/* Bottom row — density, page size, page info, pager */}
            <div className="hidden md:flex sheet-footer-row">
                <div className="flex items-center space-x-2" id="records-per-page">
                    <DensityToggle id="sheet-density-toggle" />
                    <label className="text-sm text-[var(--theme-muted)]">Rows:</label>
                    <select id="records-per-page-select" className="input-t !w-auto"
                        value={pagination.recordsPerPage} onChange={changePageSize}>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                </div>

                <div className="text-sm text-[var(--theme-muted)]" id="page-info">
                    {pagination.totalRecords === 0
                        ? "No records"
                        : `${pagination.recordsOnCurrentPage} of ${pagination.totalRecords}`}
                    {totalPages > 1 && ` · Page ${currentPage} of ${totalPages}`}
                </div>

                <div className="flex items-center space-x-1" id="pagination-controls">
                    <button className="btn-t btn-t-secondary sheet-pager-btn"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={!pagination.hasPrevPage} aria-label="Previous page">
                        <ChevronLeft size={16} />
                    </button>
                    {pageNumbers.map((p) => (
                        <button key={p} onClick={() => goToPage(p)}
                            className={`btn-t sheet-pager-btn ${p === currentPage ? "btn-t-primary" : "btn-t-secondary"}`}
                            aria-current={p === currentPage ? "page" : undefined}>
                            {p}
                        </button>
                    ))}
                    <button className="btn-t btn-t-secondary sheet-pager-btn"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={!pagination.hasNextPage} aria-label="Next page">
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
}
